import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";

function OvertimeManagement() {
	const [candidates, setCandidates] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [understaffedDays, setUnderstaffedDays] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingCandidate, setEditingCandidate] = useState(null);
	const [formData, setFormData] = useState({
		name: "",
		shiftType: "day",
		phone: "",
		departmentIds: [],
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	// OT Assignment state
	const [showOTModal, setShowOTModal] = useState(false);
	const [selectedUnderstaffed, setSelectedUnderstaffed] = useState(null);
	const [eligibleForOT, setEligibleForOT] = useState({
		currentShift: [],
		otherShift: [],
		existingOT: [],
	});
	const [loadingOT, setLoadingOT] = useState(false);
	const [shiftCoverage, setShiftCoverage] = useState("full");

	// Date filter for understaffed days
	const [filterMonth, setFilterMonth] = useState(() => {
		const now = new Date();
		return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
	});

	// OT Calendar state
	const [otAssignments, setOtAssignments] = useState([]);
	const [selectedOT, setSelectedOT] = useState(null);

	// Cascade coverage state
	const [showCascadeModal, setShowCascadeModal] = useState(false);
	const [cascadeOptions, setCascadeOptions] = useState([]);
	const [cascadeDate, setCascadeDate] = useState(null);
	const [loadingCascade, setLoadingCascade] = useState(false);
	const [applyingCascade, setApplyingCascade] = useState(false);
	// Cascade form inputs
	const [cascadeOtPerson, setCascadeOtPerson] = useState("");
	const [cascadeOtDeptId, setCascadeOtDeptId] = useState("");
	const [cascadeEligibleEmployees, setCascadeEligibleEmployees] = useState(
		[],
	);
	const [loadingEligible, setLoadingEligible] = useState(false);

	useEffect(() => {
		loadData();
	}, [filterMonth]);

	const loadData = async () => {
		try {
			// Calculate date range from filterMonth
			const [year, month] = filterMonth.split("-").map(Number);
			const startDate = new Date(year, month - 1, 1)
				.toISOString()
				.split("T")[0];
			const endDate = new Date(year, month, 0)
				.toISOString()
				.split("T")[0];

			const [candidatesRes, deptRes, understaffedRes, otAssignRes] =
				await Promise.all([
					axios.get(`${API_URL}/overtime/candidates`),
					axios.get(`${API_URL}/departments`),
					axios.get(
						`${API_URL}/schedules/understaffed?startDate=${startDate}&endDate=${endDate}`,
					),
					axios.get(
						`${API_URL}/overtime/assignments?startDate=${startDate}&endDate=${endDate}`,
					),
				]);
			setCandidates(candidatesRes.data);
			setDepartments(deptRes.data);
			setUnderstaffedDays(understaffedRes.data || []);
			setOtAssignments(otAssignRes.data || []);
		} catch (error) {
			setError("Failed to load data");
		} finally {
			setLoading(false);
		}
	};

	const openModal = (candidate = null) => {
		if (candidate) {
			setEditingCandidate(candidate);
			setFormData({
				name: candidate.name,
				shiftType: candidate.shift_type,
				phone: candidate.phone || "",
				departmentIds: candidate.departments?.map((d) => d.id) || [],
			});
		} else {
			setEditingCandidate(null);
			setFormData({
				name: "",
				shiftType: "day",
				phone: "",
				departmentIds: [],
			});
		}
		setShowModal(true);
		setError("");
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingCandidate(null);
		setError("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		if (!formData.name.trim()) {
			setError("Name is required");
			return;
		}

		if (formData.departmentIds.length === 0) {
			setError("Select at least one department");
			return;
		}

		try {
			if (editingCandidate) {
				await axios.put(
					`${API_URL}/overtime/candidates/${editingCandidate.id}`,
					formData,
				);
				setSuccess("Candidate updated");
			} else {
				await axios.post(`${API_URL}/overtime/candidates`, formData);
				setSuccess("Candidate added");
			}
			closeModal();
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(error.response?.data?.error || "Failed to save candidate");
		}
	};

	const handleDelete = async (id) => {
		try {
			await axios.delete(`${API_URL}/overtime/candidates/${id}`);
			setSuccess("Candidate removed");
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to remove candidate");
		}
	};

	const toggleDepartment = (deptId) => {
		setFormData((prev) => ({
			...prev,
			departmentIds: prev.departmentIds.includes(deptId)
				? prev.departmentIds.filter((id) => id !== deptId)
				: [...prev.departmentIds, deptId],
		}));
	};

	const getDepartmentName = (deptId) => {
		const dept = departments.find((d) => d.id === deptId);
		return dept ? dept.name : "Unknown";
	};

	// OT Assignment functions
	const openOTModal = async (understaffedDay) => {
		setSelectedUnderstaffed(understaffedDay);
		setShowOTModal(true);
		setLoadingOT(true);
		setError("");

		try {
			const dept = departments.find(
				(d) => d.name === understaffedDay.department,
			);
			if (!dept) {
				setError(
					"Could not find department: " + understaffedDay.department,
				);
				setLoadingOT(false);
				return;
			}

			const res = await axios.get(
				`${API_URL}/overtime/eligible-for-date`,
				{
					params: {
						date: understaffedDay.date,
						departmentId: dept.id,
					},
				},
			);
			setEligibleForOT(res.data);
			// Auto-select coverage based on what's already taken
			const taken = (res.data.existingOT || []).map((e) => e.coverage);
			if (
				taken.includes("full") ||
				(taken.includes("first_half") && taken.includes("second_half"))
			) {
				// fully covered already
			} else if (taken.includes("first_half")) {
				setShiftCoverage("second_half");
			} else if (taken.includes("second_half")) {
				setShiftCoverage("first_half");
			} else {
				setShiftCoverage("full");
			}
		} catch (err) {
			setError(
				"Failed to load eligible employees: " +
					(err.response?.data?.error || err.message),
			);
		} finally {
			setLoadingOT(false);
		}
	};

	const closeOTModal = () => {
		setShowOTModal(false);
		setSelectedUnderstaffed(null);
		setEligibleForOT({ currentShift: [], otherShift: [], existingOT: [] });
		setShiftCoverage("full");
	};

	// Cascade coverage functions
	const openCascadeModal = async (date) => {
		setCascadeDate(date);
		setShowCascadeModal(true);
		setCascadeOptions([]);
		setCascadeOtPerson("");
		setCascadeOtDeptId("");

		// Load eligible employees for OT on this date
		setLoadingEligible(true);
		try {
			// Get employees eligible for OT (not filtering by department since cascade can use any)
			const response = await axios.get(
				`${API_URL}/overtime/eligible-for-date`,
				{
					params: { date },
				},
			);
			// Combine current shift (off today) and other shift candidates
			const eligible = [
				...(response.data.currentShift || []).map((e) => ({
					...e,
					source: "current",
				})),
				...(response.data.otherShift || []).map((e) => ({
					...e,
					source: "other",
				})),
			];
			setCascadeEligibleEmployees(eligible);
		} catch (err) {
			console.error("Error loading eligible employees:", err);
			setCascadeEligibleEmployees([]);
		} finally {
			setLoadingEligible(false);
		}
	};

	const closeCascadeModal = () => {
		setShowCascadeModal(false);
		setCascadeDate(null);
		setCascadeOptions([]);
		setCascadeOtPerson("");
		setCascadeOtDeptId("");
		setCascadeEligibleEmployees([]);
	};

	const findCascadeOptions = async () => {
		if (!cascadeOtDeptId) {
			setError("Please select a department for the OT worker");
			return;
		}

		setLoadingCascade(true);
		try {
			const response = await axios.get(
				`${API_URL}/schedules/cascade-options/${cascadeDate}?otDeptId=${cascadeOtDeptId}&otPersonName=${encodeURIComponent(cascadeOtPerson)}`,
			);
			setCascadeOptions(response.data.options || []);
		} catch (err) {
			console.error("Error loading cascade options:", err);
			setCascadeOptions([]);
		} finally {
			setLoadingCascade(false);
		}
	};

	const applyCascade = async (option) => {
		setApplyingCascade(true);
		try {
			await axios.post(`${API_URL}/schedules/apply-cascade`, {
				date: cascadeDate,
				pivotEmployeeId: option.pivotEmployee.id,
				otEmployeeId: option.otEmployee?.id,
				otEmployeeType: option.otEmployee?.type || "manual",
				otDepartmentId: option.otDepartment.id,
				targetDepartmentId: option.targetDepartment.id,
				otPersonName: cascadeOtPerson || option.otEmployee?.name,
			});
			setSuccess(
				`Cascade applied: ${cascadeOtPerson || "OT Worker"} covers ${option.otDepartment.name}, ${option.pivotEmployee.name} moved to ${option.targetDepartment.name}`,
			);
			closeCascadeModal();
			loadData();
			setTimeout(() => setSuccess(""), 6000);
		} catch (err) {
			setError(err.response?.data?.error || "Failed to apply cascade");
		} finally {
			setApplyingCascade(false);
		}
	};

	const assignOT = async (employee) => {
		if (!selectedUnderstaffed) return;

		const dept = departments.find(
			(d) => d.name === selectedUnderstaffed.department,
		);
		if (!dept) {
			setError("Could not find department");
			return;
		}

		try {
			await axios.post(`${API_URL}/overtime/assign`, {
				employeeId: employee.id,
				employeeType: employee.type,
				date: selectedUnderstaffed.date,
				departmentId: dept.id,
				shiftCoverage,
			});

			const coverageLabel =
				shiftCoverage === "first_half"
					? "1st Half"
					: shiftCoverage === "second_half"
						? "2nd Half"
						: "Full Shift";
			setSuccess(
				`Assigned ${employee.name} to ${selectedUnderstaffed.department} on ${selectedUnderstaffed.date} (OT - ${coverageLabel})`,
			);
			closeOTModal();
			loadData();
			setTimeout(() => setSuccess(""), 5000);
		} catch (err) {
			setError(err.response?.data?.error || "Failed to assign overtime");
		}
	};

	const deleteOTAssignment = async (assignment) => {
		try {
			if (assignment.source === "candidate") {
				await axios.delete(
					`${API_URL}/overtime/candidate-coverage/${assignment.id}`,
				);
			} else {
				await axios.delete(
					`${API_URL}/overtime/assignment/${assignment.id}`,
				);
			}
			setSuccess("OT assignment removed");
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (err) {
			setError(
				err.response?.data?.error || "Failed to remove OT assignment",
			);
		}
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>Overtime Management</h1>
				<button className='btn btn-primary' onClick={() => openModal()}>
					+ Add OT Candidate
				</button>
			</div>

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			{/* Understaffed Days Section */}
			<div className='card' style={{ marginBottom: "1.5rem" }}>
				<div
					className='card-header'
					style={{
						borderBottom: "1px solid var(--gray-200)",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<h4 style={{ margin: 0, color: "var(--danger)" }}>
						⚠️ Days Needing Overtime Coverage
					</h4>
					<input
						type='month'
						className='form-input'
						value={filterMonth}
						onChange={(e) => setFilterMonth(e.target.value)}
						style={{ maxWidth: "180px" }}
					/>
				</div>
				<div className='table-container'>
					<table className='table'>
						<thead>
							<tr>
								<th>Date</th>
								<th>Department</th>
								<th>Bench</th>
								<th>Requirement</th>
								<th>Shortage</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{understaffedDays.length === 0 ? (
								<tr>
									<td
										colSpan='6'
										className='text-center text-muted'
										style={{ padding: "2rem" }}
									>
										No understaffed days for this month. 🎉
									</td>
								</tr>
							) : (
								understaffedDays.map((u, idx) => (
									<tr key={idx}>
										<td>
											{new Date(
												u.date + "T00:00:00",
											).toLocaleDateString("en-US", {
												weekday: "short",
												month: "short",
												day: "numeric",
											})}
										</td>
										<td>
											<span className='badge badge-primary'>
												{u.department}
											</span>
										</td>
										<td>{u.bench || "-"}</td>
										<td>
											{u.requirementType === "soft" ? (
												<div>
													<span className='badge badge-warning'>
														Soft
													</span>
													{u.coverageAlternatives
														?.length > 0 && (
														<div
															style={{
																fontSize:
																	"0.72rem",
																marginTop:
																	"0.2rem",
																color: "var(--gray-600)",
															}}
														>
															Can use:{" "}
															{u.coverageAlternatives.join(
																", ",
															)}
														</div>
													)}
												</div>
											) : (
												<span className='badge badge-danger'>
													Hard
												</span>
											)}
										</td>
										<td>
											<span className='badge badge-danger'>
												Need {u.shortage} more (
												{u.assigned}/{u.needed})
											</span>
										</td>
										<td>
											<div className='flex gap-sm'>
												<button
													className='btn btn-sm btn-primary'
													onClick={() =>
														openOTModal(u)
													}
												>
													Assign OT
												</button>
												<button
													className='btn btn-sm btn-secondary'
													onClick={() =>
														openCascadeModal(u.date)
													}
													title='Find cascade coverage options'
												>
													Cascade
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* OT Calendar */}
			{(() => {
				const [year, month] = filterMonth.split("-").map(Number);
				const firstDay = new Date(year, month - 1, 1);
				const daysInMonth = new Date(year, month, 0).getDate();
				const startDow = firstDay.getDay(); // 0=Sun
				const weeks = [];
				let week = new Array(startDow).fill(null);

				for (let d = 1; d <= daysInMonth; d++) {
					week.push(d);
					if (week.length === 7) {
						weeks.push(week);
						week = [];
					}
				}
				if (week.length > 0) {
					while (week.length < 7) week.push(null);
					weeks.push(week);
				}

				// Build lookup maps
				const understaffedMap = {};
				understaffedDays.forEach((u) => {
					if (!understaffedMap[u.date]) understaffedMap[u.date] = [];
					understaffedMap[u.date].push(u);
				});

				const assignedMap = {};
				otAssignments.forEach((a) => {
					if (!assignedMap[a.schedule_date])
						assignedMap[a.schedule_date] = [];
					assignedMap[a.schedule_date].push(a);
				});

				const dayNames = [
					"Sun",
					"Mon",
					"Tue",
					"Wed",
					"Thu",
					"Fri",
					"Sat",
				];

				return (
					<div className='card' style={{ marginBottom: "1.5rem" }}>
						<div
							className='card-header'
							style={{
								borderBottom: "1px solid var(--gray-200)",
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<h4 style={{ margin: 0 }}>📅 OT Calendar</h4>
							<input
								type='month'
								className='form-input'
								value={filterMonth}
								onChange={(e) => setFilterMonth(e.target.value)}
								style={{ maxWidth: "180px" }}
							/>
						</div>
						<div style={{ padding: "0.75rem", overflowX: "auto" }}>
							{/* Legend */}
							<div
								style={{
									display: "flex",
									gap: "1rem",
									marginBottom: "0.75rem",
									fontSize: "0.8rem",
									flexWrap: "wrap",
								}}
							>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
									}}
								>
									<span
										style={{
											display: "inline-block",
											width: "12px",
											height: "12px",
											background: "#ffebee",
											border: "1px solid #dc3545",
											borderRadius: "2px",
										}}
									></span>
									Needs OT
								</span>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
									}}
								>
									<span
										style={{
											display: "inline-block",
											width: "12px",
											height: "12px",
											background: "#e8f5e9",
											border: "1px solid #4caf50",
											borderRadius: "2px",
										}}
									></span>
									Fully Covered
								</span>
								<span
									style={{
										display: "flex",
										alignItems: "center",
										gap: "4px",
									}}
								>
									<span
										className='badge'
										style={{
											background: "var(--warning)",
											color: "#000",
											fontSize: "0.65rem",
											padding: "1px 4px",
										}}
									>
										OT
									</span>
									Assigned OT
								</span>
							</div>

							<table
								style={{
									width: "100%",
									borderCollapse: "collapse",
									tableLayout: "fixed",
								}}
							>
								<thead>
									<tr>
										{dayNames.map((d) => (
											<th
												key={d}
												style={{
													padding: "0.4rem",
													textAlign: "center",
													fontSize: "0.8rem",
													color: "var(--gray-500)",
													borderBottom:
														"1px solid var(--gray-200)",
												}}
											>
												{d}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{weeks.map((week, wi) => (
										<tr key={wi}>
											{week.map((day, di) => {
												if (day === null) {
													return (
														<td
															key={di}
															style={{
																padding:
																	"0.25rem",
																border: "1px solid var(--gray-100)",
															}}
														></td>
													);
												}
												const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
												const needs =
													understaffedMap[dateStr] ||
													[];
												const assigned =
													assignedMap[dateStr] || [];

												// The shortage in DB already accounts for assigned OT, so use it directly
												const hasNeeds = needs.some(
													(n) => n.shortage > 0,
												);
												// Resolved if: (1) had understaffing records but all have shortage <= 0, OR (2) has OT assignments but no understaffing record (already fully resolved in backend)
												const allResolved =
													(needs.length > 0 &&
														needs.every(
															(n) =>
																n.shortage <= 0,
														)) ||
													(assigned.length > 0 &&
														needs.length === 0);
												const isWeekend =
													di === 0 || di === 6;

												let bg = "white";
												if (allResolved) bg = "#e8f5e9";
												else if (hasNeeds)
													bg = "#ffebee";
												else if (isWeekend)
													bg = "var(--gray-50)";

												return (
													<td
														key={di}
														style={{
															padding: "0.25rem",
															border: "1px solid var(--gray-200)",
															verticalAlign:
																"top",
															background: bg,
															minHeight: "70px",
															height: "70px",
														}}
													>
														<div
															style={{
																fontSize:
																	"0.75rem",
																fontWeight: 600,
																marginBottom:
																	"2px",
																color: isWeekend
																	? "var(--gray-400)"
																	: "var(--gray-700)",
															}}
														>
															{day}
														</div>

														{/* Understaffed indicators */}
														{needs
															.filter(
																(n) =>
																	n.shortage >
																	0,
															)
															.map((n, ni) => (
																<div
																	key={`n${ni}`}
																	style={{
																		fontSize:
																			"0.75rem",
																		lineHeight: 1.3,
																		marginBottom:
																			"2px",
																	}}
																>
																	<span
																		style={{
																			color: "var(--danger)",
																			fontWeight: 700,
																		}}
																	>
																		-
																		{
																			n.shortage
																		}
																	</span>{" "}
																	<span
																		style={{
																			color: "var(--gray-700)",
																			fontWeight: 500,
																		}}
																	>
																		{n.department?.substring(
																			0,
																			4,
																		)}
																	</span>
																</div>
															))}

														{/* Assigned OT */}
														{assigned.map((a) => {
															const hours =
																a.overtime_type ===
																"first_half"
																	? "0800-1200"
																	: a.overtime_type ===
																		  "second_half"
																		? "1200-1600"
																		: "0800-1600";
															return (
																<div
																	key={`a${a.source}_${a.id}`}
																	style={{
																		fontSize:
																			"0.7rem",
																		lineHeight: 1.3,
																		marginBottom:
																			"2px",
																		display:
																			"flex",
																		alignItems:
																			"center",
																		gap: "3px",
																	}}
																>
																	<span
																		className='badge'
																		style={{
																			background:
																				"var(--warning)",
																			color: "#000",
																			fontSize:
																				"0.68rem",
																			padding:
																				"2px 5px",
																			cursor: "pointer",
																			whiteSpace:
																				"nowrap",
																		}}
																		title='Click for details'
																		onClick={() =>
																			setSelectedOT(
																				a,
																			)
																		}
																	>
																		{
																			a.employee_name
																		}{" "}
																		·{" "}
																		{
																			a.department_name
																		}{" "}
																		·{" "}
																		{hours}
																	</span>
																</div>
															);
														})}
													</td>
												);
											})}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				);
			})()}

			{/* OT Detail Popup */}
			{selectedOT &&
				(() => {
					const a = selectedOT;
					const hours =
						a.overtime_type === "first_half"
							? "0800-1200"
							: a.overtime_type === "second_half"
								? "1200-1600"
								: "0800-1600";
					const typeLabel =
						a.overtime_type === "full"
							? "Full Shift"
							: a.overtime_type === "first_half"
								? "1st Half"
								: "2nd Half";
					return (
						<div
							style={{
								position: "fixed",
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								background: "rgba(0,0,0,0.4)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								zIndex: 1000,
							}}
							onClick={() => setSelectedOT(null)}
						>
							<div
								className='card'
								style={{
									minWidth: "320px",
									maxWidth: "420px",
									margin: "1rem",
								}}
								onClick={(e) => e.stopPropagation()}
							>
								<div
									className='card-header'
									style={{
										borderBottom:
											"1px solid var(--gray-200)",
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
									}}
								>
									<h4 style={{ margin: 0 }}>
										OT Assignment Details
									</h4>
									<button
										className='btn btn-ghost'
										style={{
											padding: "0.25rem 0.5rem",
											fontSize: "1.1rem",
										}}
										onClick={() => setSelectedOT(null)}
									>
										&times;
									</button>
								</div>
								<div style={{ padding: "1rem" }}>
									<table
										style={{
											width: "100%",
											fontSize: "0.9rem",
										}}
									>
										<tbody>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
														width: "120px",
													}}
												>
													Employee
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{a.employee_name}
												</td>
											</tr>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
													}}
												>
													Department
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{a.department_name}
												</td>
											</tr>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
													}}
												>
													Date
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{a.schedule_date}
												</td>
											</tr>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
													}}
												>
													Type
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{typeLabel}
												</td>
											</tr>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
													}}
												>
													Hours
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{hours}
												</td>
											</tr>
											<tr>
												<td
													style={{
														padding: "0.4rem 0",
														fontWeight: 600,
														color: "var(--gray-500)",
													}}
												>
													Source
												</td>
												<td
													style={{
														padding: "0.4rem 0",
													}}
												>
													{a.source === "candidate"
														? "Other Shift Candidate"
														: "Current Shift Employee"}
												</td>
											</tr>
										</tbody>
									</table>
									<div
										style={{
											marginTop: "1rem",
											display: "flex",
											justifyContent: "flex-end",
											gap: "0.5rem",
										}}
									>
										<button
											className='btn btn-ghost'
											onClick={() => setSelectedOT(null)}
										>
											Close
										</button>
										<button
											style={{
												background: "#dc3545",
												color: "white",
												border: "none",
												padding: "0.5rem 1rem",
												borderRadius: "6px",
												cursor: "pointer",
												fontWeight: 600,
												fontSize: "0.9rem",
											}}
											onClick={() => {
												deleteOTAssignment(a);
												setSelectedOT(null);
											}}
										>
											Delete Assignment
										</button>
									</div>
								</div>
							</div>
						</div>
					);
				})()}

			{/* Other Shift Candidates Section */}
			<div className='card'>
				<div
					className='card-header'
					style={{ borderBottom: "1px solid var(--gray-200)" }}
				>
					<h4 style={{ margin: 0 }}>Other Shift Candidates</h4>
					<p
						className='text-muted text-sm'
						style={{ margin: "0.25rem 0 0 0" }}
					>
						People from other shifts who can be called for overtime
						coverage
					</p>
				</div>
				<div className='table-container'>
					<table className='table'>
						<thead>
							<tr>
								<th>Name</th>
								<th>Regular Shift</th>
								<th>Phone</th>
								<th>Can Work In</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{candidates.length === 0 ? (
								<tr>
									<td
										colSpan='5'
										className='text-center text-muted'
										style={{ padding: "2rem" }}
									>
										No other-shift candidates added yet.
									</td>
								</tr>
							) : (
								candidates.map((candidate) => (
									<tr key={candidate.id}>
										<td className='font-medium'>
											{candidate.name}
										</td>
										<td>
											<span className='badge badge-secondary'>
												{candidate.shift_type === "day"
													? "Day"
													: candidate.shift_type ===
														  "evening"
														? "Evening"
														: "Night"}
											</span>
										</td>
										<td>{candidate.phone || "-"}</td>
										<td>
											<div
												className='flex gap-xs'
												style={{ flexWrap: "wrap" }}
											>
												{candidate.departments?.map(
													(dept) => (
														<span
															key={dept.id}
															className='badge badge-primary'
															style={{
																fontSize:
																	"0.7rem",
															}}
														>
															{dept.name}
														</span>
													),
												)}
											</div>
										</td>
										<td>
											<div className='flex gap-sm'>
												<button
													className='btn btn-sm btn-secondary'
													onClick={() =>
														openModal(candidate)
													}
												>
													Edit
												</button>
												<button
													className='btn btn-sm btn-danger'
													onClick={() =>
														handleDelete(
															candidate.id,
														)
													}
												>
													Remove
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Add/Edit Candidate Modal */}
			{showModal && (
				<div className='modal-overlay' onClick={closeModal}>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "500px" }}
					>
						<div className='modal-header'>
							<h3>
								{editingCandidate
									? "Edit Candidate"
									: "Add Candidate"}
							</h3>
							<button
								className='modal-close'
								onClick={closeModal}
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleSubmit}>
							<div className='modal-body'>
								{error && (
									<div className='alert alert-error'>
										{error}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>Name *</label>
									<input
										type='text'
										className='form-input'
										value={formData.name}
										onChange={(e) =>
											setFormData({
												...formData,
												name: e.target.value,
											})
										}
										placeholder='Full name'
										required
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Regular Shift *
									</label>
									<select
										className='form-select'
										value={formData.shiftType}
										onChange={(e) =>
											setFormData({
												...formData,
												shiftType: e.target.value,
											})
										}
									>
										<option value='day'>Day Shift</option>
										<option value='evening'>
											Evening Shift
										</option>
										<option value='night'>
											Night Shift
										</option>
									</select>
								</div>

								<div className='form-group'>
									<label className='form-label'>Phone</label>
									<input
										type='tel'
										className='form-input'
										value={formData.phone}
										onChange={(e) =>
											setFormData({
												...formData,
												phone: e.target.value,
											})
										}
										placeholder='Contact number'
									/>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Departments They Can Work *
									</label>
									<div
										style={{
											display: "grid",
											gridTemplateColumns:
												"repeat(2, 1fr)",
											gap: "0.5rem",
										}}
									>
										{departments.map((dept) => (
											<label
												key={dept.id}
												className='form-checkbox'
												style={{
													padding: "0.5rem 0.75rem",
													background:
														formData.departmentIds.includes(
															dept.id,
														)
															? "var(--primary-100)"
															: "var(--gray-100)",
													borderRadius:
														"var(--radius-md)",
													cursor: "pointer",
													display: "flex",
													alignItems: "center",
													gap: "0.5rem",
												}}
											>
												<input
													type='checkbox'
													checked={formData.departmentIds.includes(
														dept.id,
													)}
													onChange={() =>
														toggleDepartment(
															dept.id,
														)
													}
												/>
												<span>{dept.name}</span>
											</label>
										))}
									</div>
								</div>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-secondary'
									onClick={closeModal}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='btn btn-primary'
								>
									{editingCandidate ? "Update" : "Add"}{" "}
									Candidate
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{/* OT Assignment Modal */}
			{showOTModal && selectedUnderstaffed && (
				<div className='modal-overlay' onClick={closeOTModal}>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "550px" }}
					>
						<div className='modal-header'>
							<h3>Assign Overtime Coverage</h3>
							<button
								className='modal-close'
								onClick={closeOTModal}
							>
								&times;
							</button>
						</div>
						<div className='modal-body'>
							<div
								className='card'
								style={{
									padding: "0.75rem",
									marginBottom: "1rem",
								}}
							>
								<div className='flex gap-md'>
									<div>
										<small className='text-muted'>
											Date
										</small>
										<div className='font-medium'>
											{new Date(
												selectedUnderstaffed.date +
													"T00:00:00",
											).toLocaleDateString("en-US", {
												weekday: "long",
												month: "short",
												day: "numeric",
											})}
										</div>
									</div>
									<div>
										<small className='text-muted'>
											Department
										</small>
										<div className='font-medium'>
											{selectedUnderstaffed.department}
										</div>
									</div>
									<div>
										<small className='text-muted'>
											Bench
										</small>
										<div className='font-medium'>
											{selectedUnderstaffed.bench || "-"}
										</div>
									</div>
									<div>
										<small className='text-muted'>
											Need
										</small>
										<div className='font-medium'>
											{selectedUnderstaffed.shortage} more
										</div>
									</div>
								</div>
							</div>

							{loadingOT ? (
								<div
									className='text-center'
									style={{ padding: "2rem" }}
								>
									<div className='spinner'></div>
									<p className='text-muted'>
										Loading eligible employees...
									</p>
								</div>
							) : (
								<>
									{/* Existing OT Coverage */}
									{eligibleForOT.existingOT?.length > 0 && (
										<div
											style={{
												marginBottom: "1rem",
												padding: "0.6rem 0.75rem",
												background: "var(--gray-50)",
												borderRadius: "6px",
												border: "1px solid var(--gray-200)",
											}}
										>
											<div
												style={{
													fontWeight: 600,
													fontSize: "0.85rem",
													marginBottom: "0.35rem",
												}}
											>
												Already Assigned
											</div>
											{eligibleForOT.existingOT.map(
												(e, i) => (
													<div
														key={i}
														style={{
															fontSize: "0.82rem",
															color: "var(--gray-600)",
														}}
													>
														•{" "}
														<strong>
															{e.name}
														</strong>{" "}
														—{" "}
														{e.coverage === "full"
															? "Full Shift"
															: e.coverage ===
																  "first_half"
																? "1st Half"
																: "2nd Half"}
													</div>
												),
											)}
										</div>
									)}

									{/* Shift Coverage Selector */}
									{(() => {
										const taken = (
											eligibleForOT.existingOT || []
										).map((e) => e.coverage);
										const fullyCovered =
											taken.includes("full") ||
											(taken.includes("first_half") &&
												taken.includes("second_half"));
										if (fullyCovered) {
											return (
												<div
													className='alert alert-success'
													style={{
														marginBottom: "1rem",
													}}
												>
													✅ This day/department is
													fully covered — no more OT
													needed.
												</div>
											);
										}
										return (
											<div
												style={{
													marginBottom: "1.25rem",
												}}
											>
												<label
													style={{
														display: "block",
														fontWeight: 600,
														marginBottom: "0.5rem",
													}}
												>
													Shift Coverage
												</label>
												<div className='flex gap-sm'>
													{[
														{
															value: "full",
															label: "Full Shift",
															desc: "8:00 AM – 4:00 PM",
														},
														{
															value: "first_half",
															label: "1st Half",
															desc: "8:00 AM – 12:00 PM",
														},
														{
															value: "second_half",
															label: "2nd Half",
															desc: "12:00 PM – 4:00 PM",
														},
													].map((opt) => {
														const isTaken =
															taken.includes(
																opt.value,
															) ||
															(opt.value !==
																"full" &&
																taken.includes(
																	"full",
																)) ||
															(opt.value ===
																"full" &&
																taken.length >
																	0);
														return (
															<button
																key={opt.value}
																type='button'
																disabled={
																	isTaken
																}
																onClick={() =>
																	!isTaken &&
																	setShiftCoverage(
																		opt.value,
																	)
																}
																style={{
																	flex: 1,
																	padding:
																		"0.6rem 0.5rem",
																	border: `2px solid ${isTaken ? "var(--gray-200)" : shiftCoverage === opt.value ? "var(--primary)" : "var(--gray-300)"}`,
																	borderRadius:
																		"6px",
																	background:
																		isTaken
																			? "var(--gray-100)"
																			: shiftCoverage ===
																				  opt.value
																				? "var(--primary-50, #eff6ff)"
																				: "white",
																	color: isTaken
																		? "var(--gray-400)"
																		: shiftCoverage ===
																			  opt.value
																			? "var(--primary)"
																			: "var(--gray-600)",
																	fontWeight:
																		shiftCoverage ===
																		opt.value
																			? 700
																			: 400,
																	cursor: isTaken
																		? "not-allowed"
																		: "pointer",
																	textAlign:
																		"center",
																	lineHeight: 1.3,
																	position:
																		"relative",
																}}
															>
																<div>
																	{opt.label}
																</div>
																<div
																	style={{
																		fontSize:
																			"0.7rem",
																		opacity: 0.75,
																	}}
																>
																	{opt.desc}
																</div>
																{isTaken && (
																	<div
																		style={{
																			fontSize:
																				"0.65rem",
																			color: "var(--gray-400)",
																			marginTop:
																				"2px",
																		}}
																	>
																		Taken
																	</div>
																)}
															</button>
														);
													})}
												</div>
												{shiftCoverage !== "full" &&
													!taken.includes(
														shiftCoverage,
													) && (
														<p
															style={{
																margin: "0.4rem 0 0",
																fontSize:
																	"0.8rem",
																color: "var(--warning-600, #b45309)",
															}}
														>
															⚠ Half-shift
															assignment — the
															other half will
															still need to be
															filled.
														</p>
													)}
											</div>
										);
									})()}
									{eligibleForOT.currentShift?.length > 0 && (
										<div style={{ marginBottom: "1rem" }}>
											<h4
												style={{
													marginBottom: "0.5rem",
												}}
											>
												Current Shift Employees (Not
												Scheduled)
											</h4>
											<div
												style={{
													display: "grid",
													gap: "0.5rem",
												}}
											>
												{eligibleForOT.currentShift.map(
													(emp) => (
														<div
															key={emp.id}
															className='card'
															style={{
																padding:
																	"0.75rem",
																display: "flex",
																justifyContent:
																	"space-between",
																alignItems:
																	"center",
															}}
														>
															<span className='font-medium'>
																{emp.name}
															</span>
															<button
																className='btn btn-sm btn-success'
																onClick={() =>
																	assignOT({
																		...emp,
																		type: "current",
																	})
																}
															>
																Assign
															</button>
														</div>
													),
												)}
											</div>
										</div>
									)}

									{eligibleForOT.otherShift?.length > 0 && (
										<div>
											<h4
												style={{
													marginBottom: "0.5rem",
												}}
											>
												Other Shift Candidates
											</h4>
											<div
												style={{
													display: "grid",
													gap: "0.5rem",
												}}
											>
												{eligibleForOT.otherShift.map(
													(emp) => (
														<div
															key={emp.id}
															className='card'
															style={{
																padding:
																	"0.75rem",
																display: "flex",
																justifyContent:
																	"space-between",
																alignItems:
																	"center",
															}}
														>
															<div>
																<span className='font-medium'>
																	{emp.name}
																</span>
																<span
																	className='badge badge-secondary'
																	style={{
																		marginLeft:
																			"0.5rem",
																		fontSize:
																			"0.7rem",
																	}}
																>
																	{emp.shift_type ===
																	"day"
																		? "Day"
																		: emp.shift_type ===
																			  "evening"
																			? "Evening"
																			: "Night"}{" "}
																	Shift
																</span>
																{emp.phone && (
																	<div
																		className='text-xs text-muted'
																		style={{
																			marginTop:
																				"0.25rem",
																		}}
																	>
																		📞{" "}
																		{
																			emp.phone
																		}
																	</div>
																)}
															</div>
															<button
																className='btn btn-sm btn-success'
																onClick={() =>
																	assignOT({
																		...emp,
																		type: "other",
																	})
																}
															>
																Assign
															</button>
														</div>
													),
												)}
											</div>
										</div>
									)}

									{(!eligibleForOT.currentShift ||
										eligibleForOT.currentShift.length ===
											0) &&
										(!eligibleForOT.otherShift ||
											eligibleForOT.otherShift.length ===
												0) && (
											<div
												className='text-center text-muted'
												style={{ padding: "2rem" }}
											>
												No employees available for
												overtime on this date.
												<br />
												<small>
													Add other shift candidates
													above to expand your pool.
												</small>
											</div>
										)}
								</>
							)}
						</div>
						<div className='modal-footer'>
							<button
								className='btn btn-secondary'
								onClick={closeOTModal}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Cascade Options Modal */}
			{showCascadeModal && (
				<div className='modal-overlay' onClick={closeCascadeModal}>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "650px" }}
					>
						<div className='modal-header'>
							<h3>Cascade Coverage</h3>
							<button
								className='modal-close'
								onClick={closeCascadeModal}
							>
								&times;
							</button>
						</div>
						<div className='modal-body'>
							<div
								className='card'
								style={{
									padding: "0.75rem",
									marginBottom: "1rem",
									background: "var(--gray-50)",
								}}
							>
								<p style={{ margin: 0, fontSize: "0.85rem" }}>
									<strong>How it works:</strong> Enter who's
									doing OT and which department they want to
									work. We'll show you how to reassign regular
									staff to cover shortages.
								</p>
							</div>

							<div style={{ marginBottom: "1rem" }}>
								<strong>Date:</strong>{" "}
								{cascadeDate &&
									new Date(
										cascadeDate + "T00:00:00",
									).toLocaleDateString("en-US", {
										weekday: "long",
										month: "short",
										day: "numeric",
										year: "numeric",
									})}
							</div>

							{/* OT Person Input */}
							<div
								className='form-group'
								style={{ marginBottom: "1rem" }}
							>
								<label
									className='form-label'
									style={{ fontWeight: 600 }}
								>
									Who is doing overtime?
								</label>
								{loadingEligible ? (
									<div
										className='text-muted'
										style={{ padding: "0.5rem" }}
									>
										Loading eligible employees...
									</div>
								) : (
									<>
										<select
											className='form-select'
											value={cascadeOtPerson}
											onChange={(e) =>
												setCascadeOtPerson(
													e.target.value,
												)
											}
											style={{ marginBottom: "0.5rem" }}
										>
											<option value=''>
												-- Select or type below --
											</option>
											{cascadeEligibleEmployees.map(
												(emp) => (
													<option
														key={`${emp.source}-${emp.id}`}
														value={emp.name}
													>
														{emp.name}{" "}
														{emp.source === "other"
															? `(${emp.shift_type} shift)`
															: "(off today)"}
													</option>
												),
											)}
										</select>
										<input
											type='text'
											className='form-input'
											value={cascadeOtPerson}
											onChange={(e) =>
												setCascadeOtPerson(
													e.target.value,
												)
											}
											placeholder='Or type a name (e.g., someone not in system)'
										/>
									</>
								)}
							</div>

							{/* Department Selection - filtered by selected employee's training */}
							<div
								className='form-group'
								style={{ marginBottom: "1.25rem" }}
							>
								<label
									className='form-label'
									style={{ fontWeight: 600 }}
								>
									Which department will they work in?
								</label>
								<select
									className='form-select'
									value={cascadeOtDeptId}
									onChange={(e) =>
										setCascadeOtDeptId(e.target.value)
									}
									disabled={!cascadeOtPerson}
								>
									<option value=''>
										{cascadeOtPerson ? "-- Select department --" : "-- Select employee first --"}
									</option>
									{(() => {
										// Find selected employee and filter to their trained departments
										const selectedEmp = cascadeEligibleEmployees.find(
											(e) => e.name === cascadeOtPerson
										);
										const trainedDeptIds = selectedEmp?.departments?.map(d => d.id) || [];
										
										// If employee is selected and has departments, filter. Otherwise show all.
										const deptsToShow = trainedDeptIds.length > 0
											? departments.filter(d => trainedDeptIds.includes(d.id))
											: departments;
										
										return deptsToShow.map((dept) => (
											<option key={dept.id} value={dept.id}>
												{dept.name}
											</option>
										));
									})()}
								</select>
								{cascadeOtPerson && (() => {
									const selectedEmp = cascadeEligibleEmployees.find(
										(e) => e.name === cascadeOtPerson
									);
									if (!selectedEmp?.departments?.length && !cascadeEligibleEmployees.find(e => e.name === cascadeOtPerson)) {
										return (
											<small className="text-muted" style={{ display: "block", marginTop: "0.25rem" }}>
												Manually typed name - all departments available
											</small>
										);
									}
									return null;
								})()}
							</div>

							{/* Find Options Button */}
							<div style={{ marginBottom: "1.5rem" }}>
								<button
									className='btn btn-primary'
									onClick={findCascadeOptions}
									disabled={
										!cascadeOtDeptId || loadingCascade
									}
									style={{ width: "100%" }}
								>
									{loadingCascade
										? "Finding Options..."
										: "Find Cascade Options"}
								</button>
							</div>

							{/* Results */}
							{cascadeOptions.length > 0 && (
								<>
									<hr
										style={{
											margin: "1rem 0",
											borderColor: "var(--gray-200)",
										}}
									/>
									<h4 style={{ marginBottom: "0.75rem" }}>
										Available Options (
										{cascadeOptions.length})
									</h4>
									<div
										style={{
											display: "grid",
											gap: "0.75rem",
										}}
									>
										{cascadeOptions.map((option, idx) => (
											<div
												key={idx}
												className='card'
												style={{
													padding: "1rem",
													border: "2px solid var(--gray-200)",
												}}
											>
												<div
													style={{
														marginBottom: "0.75rem",
													}}
												>
													<div
														style={{
															fontWeight: 600,
															marginBottom:
																"0.5rem",
															color: "var(--primary)",
														}}
													>
														Option {idx + 1}
													</div>
													<div
														style={{
															fontSize: "0.9rem",
															lineHeight: 1.6,
														}}
													>
														<div
															style={{
																marginBottom:
																	"0.35rem",
															}}
														>
															<span
																className='badge badge-warning'
																style={{
																	marginRight:
																		"0.5rem",
																}}
															>
																OT
															</span>
															<strong>
																{cascadeOtPerson ||
																	"OT Worker"}
															</strong>{" "}
															works overtime in{" "}
															<strong>
																{
																	option
																		.otDepartment
																		.name
																}
															</strong>
														</div>
														<div
															style={{
																marginBottom:
																	"0.35rem",
															}}
														>
															<span
																className='badge badge-secondary'
																style={{
																	marginRight:
																		"0.5rem",
																}}
															>
																Move
															</span>
															<strong>
																{
																	option
																		.pivotEmployee
																		.name
																}
															</strong>{" "}
															moves from{" "}
															<strong>
																{
																	option
																		.otDepartment
																		.name
																}
															</strong>{" "}
															to{" "}
															<strong>
																{
																	option
																		.targetDepartment
																		.name
																}
															</strong>
														</div>
														<div>
															<span
																className='badge badge-success'
																style={{
																	marginRight:
																		"0.5rem",
																}}
															>
																Result
															</span>
															Covers shortage in{" "}
															<strong>
																{
																	option
																		.targetDepartment
																		.name
																}
															</strong>
														</div>
													</div>
												</div>
												<div
													style={{
														display: "flex",
														justifyContent:
															"flex-end",
													}}
												>
													<button
														className='btn btn-sm btn-success'
														disabled={
															applyingCascade
														}
														onClick={() =>
															applyCascade({
																...option,
																otEmployee: {
																	...option.otEmployee,
																	name:
																		cascadeOtPerson ||
																		option
																			.otEmployee
																			?.name ||
																		"OT Worker",
																},
															})
														}
													>
														{applyingCascade
															? "Applying..."
															: "Apply This Cascade"}
													</button>
												</div>
											</div>
										))}
									</div>
								</>
							)}

							{/* No results message - only show after search */}
							{cascadeOptions.length === 0 &&
								cascadeOtDeptId &&
								!loadingCascade && (
									<div
										className='text-center text-muted'
										style={{
											padding: "1rem",
											background: "var(--gray-50)",
											borderRadius: "6px",
										}}
									>
										No cascade options found for{" "}
										{departments.find(
											(d) =>
												d.id ===
												parseInt(cascadeOtDeptId),
										)?.name || "this department"}
										.
										<br />
										<small>
											Try selecting a different department
											where a currently-assigned employee
											works.
										</small>
									</div>
								)}
						</div>
						<div className='modal-footer'>
							<button
								className='btn btn-secondary'
								onClick={closeCascadeModal}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default OvertimeManagement;
